import { api } from "./api";
import { isAxiosError } from "axios";
import {
  Task,
  User,
  Author,
  Publisher,
  Claim,
  ReferenceWithClaims,
  AuthorRating,
  PublisherRating,
  ClaimLinks,
  DiscussionEntry,
  LinkedClaim,
} from "../../../shared/entities/types";

const API_BASE_URL = import.meta.env.VITE_BASE_URL || "https://localhost:5001";

export interface ProvenanceTemporalValue {
  value: string;
  precision: "year" | "month" | "date" | "datetime";
  origin: string | null;
  uncertain: boolean;
}

export interface ProvenanceEvidenceLink {
  signal_source: string;
  relationship_id: number | null;
  reference_claim_id: number | null;
  supporting_assertion: string | null;
  stance: "support" | "refute" | "nuance" | "insufficient" | null;
  score: number | null;
  confidence: number | null;
  support_level: number | null;
  rationale: string | null;
  quote: string | null;
  created_by_ai: boolean | null;
  review_status: "proposed" | "approved" | "rejected" | "superseded" | null;
}

export interface ProvenanceSource {
  content_id: number;
  title: string;
  publisher: string | null;
  publisher_id: number | null;
  url: string | null;
  content_type: string;
  source_crest: {
    admiraltyCode?: string | null;
    alignment?: unknown;
    evaluationStatus?: string | null;
  } | null;
  temporal: {
    published: ProvenanceTemporalValue | null;
    modified: ProvenanceTemporalValue | null;
    retrieved: ProvenanceTemporalValue | null;
  };
  chronology: (ProvenanceTemporalValue & {
    kind: "published" | "modified_fallback" | "retrieved_fallback";
  }) | null;
  classifications: Array<"direct_text" | "semantic_candidate" | "evidence_linked_candidate">;
  direct_text: {
    passage: string;
    passage_start: null;
    passage_end: null;
    representation: { type: string; version: string; sha256: string };
  } | null;
  semantic_candidates: Array<{
    claim_id: number;
    claim_text: string;
    claim_type: string;
    similarity: number | null;
  }>;
  evidence_links: ProvenanceEvidenceLink[];
}

export interface ProvenanceMapResponse {
  case: { content_id: number; title: string };
  selected_assertion: {
    claim_id: number;
    text: string;
    historical_time: {
      start: { value: string; precision: ProvenanceTemporalValue["precision"] };
      end: { value: string; precision: ProvenanceTemporalValue["precision"] } | null;
      origin: string;
      uncertain: boolean;
    } | null;
  };
  sources: ProvenanceSource[];
  timeline: Array<{
    content_id: number;
    title: string;
    chronology: ProvenanceSource["chronology"];
    classifications: ProvenanceSource["classifications"];
  }>;
  coverage: {
    embedding_model: string;
    eligible_assertions: number;
    embedded_assertions: number;
    complete: boolean;
    selected_assertion_embedded: boolean;
    semantic_display_floor: number;
  };
  limitations: string[];
}

export async function fetchProvenanceMap(
  contentId: number,
  claimId: number,
): Promise<ProvenanceMapResponse> {
  const response = await api.get<ProvenanceMapResponse>(
    `/api/content/${contentId}/claims/${claimId}/provenance-map`,
  );
  return response.data;
}

//contentscores
export type VerimeterMode = 'ai' | 'user' | 'combined';

export const fetchContentScores = async (
  contentId: number,
  userId: number | null,
  mode: VerimeterMode = 'combined',
  aiWeight: number = 0.5
) => {
  let url = `${API_BASE_URL}/api/content/${contentId}/scores`;

  // Use mode-specific endpoints
  if (mode === 'ai') {
    url = `${API_BASE_URL}/api/content/${contentId}/scores/ai`;
  } else if (mode === 'user') {
    url = `${API_BASE_URL}/api/content/${contentId}/scores/user`;
    if (userId) {
      url += `?viewerId=${userId}`;
    }
  } else if (mode === 'combined') {
    url = `${API_BASE_URL}/api/content/${contentId}/scores/combined`;
    const params = new URLSearchParams();
    if (userId) params.append('viewerId', userId.toString());
    params.append('aiWeight', aiWeight.toString());
    url += `?${params.toString()}`;
  }

  const res = await fetch(url);
  if (!res.ok) throw new Error("Failed to fetch content scores");
  const data = await res.json();
  return {
    verimeterScore: Number(data.verimeter_score) || 0,
    trollmeterScore: Number(data.trollmeter_score) || 0,
    pro: Number(data.pro_score) || 0,
    con: Number(data.con_score) || 0,
    contentId: Number(contentId),
    mode: data.mode || mode,
    ratingCounts: data.rating_counts,
  };
};

export type ClaimVerdict = "true" | "false" | "uncertain";

export async function saveClaimVerification(input: {
  claim_id: number;
  user_id?: number | null;
  verdict: ClaimVerdict;
  confidence: number; // 0..1
  notes?: string;
}) {
  const res = await fetch(`${API_BASE_URL}/api/claim-verifications`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      claim_id: input.claim_id,
      user_id: input.user_id ?? null,
      verdict: input.verdict,
      confidence: input.confidence,
      notes: input.notes ?? "",
    }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Failed to save claim verification: ${res.status} ${text}`);
  }
  return res.json();
}

export async function updateScoresForContent(
  contentId: number,
  userId: number | null
): Promise<void> {
  await fetch(`${API_BASE_URL}/api/content/${contentId}/scores/recompute`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ userId }), // 👈 pass userId to backend
  });
}
//task claims to reference links

//UPLOAD IMAGES
export const uploadImage = async (
  id: number,
  file: File,
  type: "authors" | "publishers" | "content"
): Promise<string | null> => {
  const formData = new FormData();
  formData.append("image", file); // ✅ only append the file here

  try {
    const response = await api.post(
      `${API_BASE_URL}/api/upload-image?type=${type}&id=${id}`,
      formData,
    );
    return response.data.path;
  } catch (error) {
    console.error("Image upload failed", error);
    return null;
  }
};

/**
 * Fetch Verimeter Score for a given task (by content_id)
 */
export const fetchVerimeterScore = async (taskContentId: number) => {
  const res = await fetch(`${API_BASE_URL}/api/verimeter/${taskContentId}`);
  if (!res.ok) throw new Error("Failed to fetch Verimeter score");
  return await res.json();
};
export const fetchClaimScores = async (
  contentId: number,
  viewerId: number | null
): Promise<{ [claimId: number]: number }> => {
  try {
    const res = await api.get(
      `${API_BASE_URL}/api/content/${contentId}/claim-scores`,
      {
        params: { viewerId },
      }
    );
    return res.data;
  } catch (err) {
    console.error("Error in fetchClaimScores:", err);
    return {};
  }
};
/**
 * Fetch Trollmeter Score (crowd sentiment) for a given task (by content_id)
 */
export const fetchTrollmeterScore = async (taskContentId: number) => {
  const res = await fetch(`${API_BASE_URL}/api/trollmeter/${taskContentId}`);
  if (!res.ok) throw new Error("Failed to fetch Trollmeter score");
  return await res.json();
};

// 🔵 AUTHOR BIO
export const updateAuthorBio = async (authorId: number, newBio: string) => {
  const res = await api.put(`${API_BASE_URL}/api/authors/${authorId}/bio`, {
    description: newBio,
  });
  return res.data;
};
// 🔵 PUBLISHER BIO
export const updatePublisherBio = async (
  publisherId: number,
  newBio: string
) => {
  const res = await api.put(
    `${API_BASE_URL}/api/publishers/${publisherId}/bio`,
    {
      description: newBio,
    }
  );
  return res.data;
};

// 🔵 AUTHOR RATINGS
export const fetchAuthorRatings = async (
  authorId: number
): Promise<AuthorRating[]> => {
  const res = await api.get(
    `${API_BASE_URL}/api/authors/${authorId}/ratings`
  );
  return res.data;
};

//update all author ratings
export const updateAuthorRatings = async (
  authorId: number,
  ratings: AuthorRating[]
) => {
  const res = await api.put(
    `${API_BASE_URL}/api/authors/${authorId}/ratings`,
    { ratings }
  );
  return res.data;
};

//update single author rating
export const updateAuthorRating = async (
  authorRatingId: number,
  ratings: AuthorRating
) => {
  let res;
  if (authorRatingId !== 0) {
    res = await api.put(
      `${API_BASE_URL}/api/authors/${authorRatingId}/rating`,
      { ratings }
    );
  } else {
    //add a new rating
    res = await api.post(`${API_BASE_URL}/api/authors/add-rating`, {
      ratings,
    });
  }
  return res.data;
};

// 🔵 PUBLISHER RATINGS
export const fetchPublisherRatings = async (publisherId: number) => {
  try {
    const res = await api.get(
      `${API_BASE_URL}/api/publishers/${publisherId}/ratings`
    );

    return res.data;
  } catch (err) {
    console.error("❌ Failed to fetch publisher ratings:", err);
    return [];
  }
};

//get ratings topics

export const fetchRatingTopics = async () => {
  const res = await api.get(`${API_BASE_URL}/api/publishers/ratings-topics`);
  return res.data;
};

export const updatePublisherRatings = async (
  publisherId: number,
  ratings: PublisherRating[]
) => {
  const res = await api.put(
    `${API_BASE_URL}/api/publishers/${publisherId}/ratings`,
    {
      ratings,
    }
  );
  return res.data;
};

//update a single publisher rating
export const updatePublisherRating = async (
  publisherRatingId: number,
  rating: PublisherRating
) => {
  const res = await api.put(
    `${API_BASE_URL}/api/publishers/${publisherRatingId}/rating`,
    {
      rating,
    }
  );
  return res.data;
};
/** --------------------- 📝 TASK (CONTENT) FUNCTIONS --------------------- **/
/** fetch one task  */
/** fetch one task  */
// services/useDashboardAPI.ts

export async function fetchTaskById(taskId: number): Promise<Task> {
  const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}`);
  if (!response.ok) throw new Error("Failed to fetch task");
  return response.json();
}
// services/useDashboardAPI.ts

// ✅ useDashboardAPI.ts

//Fetch all taskinfo by publisher, author or task id
export async function fetchUnifiedTasksByPivot(
  pivotType: "task" | "author" | "publisher" | "reference",
  pivotId: number
): Promise<Task[]> {
  const response = await fetch(
    `${API_BASE_URL}/api/unified-tasks/${pivotType}/${pivotId}`
  );

  if (!response.ok) throw new Error("Failed to fetch unified tasks");
  return response.json();
}
export interface RatedClaim {
  claim_id: number;
  claim_text: string;
  verimeter_score: number; // Expected truth score (-1 to 1)
}

export async function fetchTaskClaimsWithRatings(
  taskId: number,
  viewerId: number
): Promise<RatedClaim[]> {
  const res = await fetch(
    `${API_BASE_URL}/api/task/${taskId}/claims?viewerId=${viewerId}`
  );
  if (!res.ok) throw new Error("Failed to fetch claims");
  return res.json();
}

/**
 * Fetch all tasks (content items).
 */
export const fetchTasks = async (page = 1, limit = 100): Promise<Task[]> => {
  try {
    const response = await api.get(`${API_BASE_URL}/api/content`, {
      params: { page, limit },
    });
    return response.data;
  } catch (error) {
    console.error("❌ Error fetching paginated tasks:", error);
    return [];
  }
};

/**
 * Fetch a single task by ID.
 */
export const fetchTask = async (contentId: number): Promise<Task | null> => {
  try {
    const response = await api.get(`${API_BASE_URL}/api/content/${contentId}`);
    return response.data;
  } catch (error) {
    console.error("❌ Error fetching task:", error);
    return null;
  }
};

/**
 * Fetch all assigned users for a given task.
 */
export const fetchAssignedUsers = async (taskId: number): Promise<User[]> => {
  try {
    const response = await api.get(
      `${API_BASE_URL}/api/content/${taskId}/get-users`
    );
    return response.data;
  } catch (error) {
    console.error("❌ Error fetching assigned users:", error);
    return [];
  }
};

/**
 * fetch all tasks for a user
 *
 */

export const fetchTasksForUser = async (userId: number, showInactive = false): Promise<Task[]> => {
  const res = await api.get(`${API_BASE_URL}/api/user-tasks/${userId}`, {
    params: { showInactive: showInactive ? 'true' : 'false' }
  });
  return res.data;
};

export interface CaseCardPage {
  items: Task[];
  nextCursor: number | null;
}

const CASE_CARD_CACHE_TTL_MS = 60_000;
const caseCardRequests = new Map<string, Promise<CaseCardPage>>();
const caseCardCache = new Map<
  string,
  { expiresAt: number; page: CaseCardPage }
>();

export const fetchCaseCards = (
  viewerId: number,
  scope: "all" | "assigned",
  showInactive = false,
  options: { cursor?: number | null; topic?: string; limit?: number } = {},
): Promise<CaseCardPage> => {
  const cursor = options.cursor ?? null;
  const topic = options.topic?.trim() || "";
  const limit = options.limit ?? 24;
  const requestKey = `${viewerId}:${scope}:${showInactive}:${topic}:${cursor ?? "first"}:${limit}`;
  const cached = caseCardCache.get(requestKey);
  if (cached && cached.expiresAt > Date.now()) return Promise.resolve(cached.page);
  if (cached) caseCardCache.delete(requestKey);

  const pendingRequest = caseCardRequests.get(requestKey);
  if (pendingRequest) return pendingRequest;

  const request = api
    .get<CaseCardPage>(`${API_BASE_URL}/api/task-cards`, {
      params: {
        viewerId,
        scope,
        showInactive: showInactive ? "true" : "false",
        cursor: cursor ?? undefined,
        topic: topic || undefined,
        limit,
      },
    })
    .then((response) => {
      caseCardCache.set(requestKey, {
        expiresAt: Date.now() + CASE_CARD_CACHE_TTL_MS,
        page: response.data,
      });
      return response.data;
    })
    .finally(() => caseCardRequests.delete(requestKey));

  caseCardRequests.set(requestKey, request);
  return request;
};

/** --------------------- 🔎 CLAIMS FUNCTIONS --------------------- **/
//fetch claims linked to a certain task claim
export interface LinkedClaim1 {
  claim_link_id: number;
  target_claim_id: number;
  source_claim_id: number;
  relationship: string;
  support_level: number;
  veracity: number | null;
  bias: number | null;
  notes: string | null;
  claim_text: string;
  reference_content_id: number;
}

export const fetchLinkedClaimsForTask = async (
  contentId: number,
  viewerId?: number | null
): Promise<LinkedClaim[]> => {
  const url = new URL(
    `${API_BASE_URL}/api/linked-claims-for-task/${contentId}`
  );
  if (viewerId) url.searchParams.append("viewerId", viewerId.toString());

  const res = await fetch(url.toString());
  if (!res.ok) {
    throw new Error("Failed to fetch linked claims");
  }

  return await res.json();
};

export const fetchLiveVerimeterScore = async (
  claimId: number,
  viewerId: number | null
): Promise<{
  verimeter_score: number;
  num_links: number;
  num_references: number;
  avg_reference_veracity: number;
  avg_reference_bias: number;
}> => {
  const qs =
    viewerId != null ? `?viewerId=${encodeURIComponent(viewerId)}` : "";
  const res = await fetch(
    `${API_BASE_URL}/api/live-verimeter-score/${claimId}${qs}`
  );
  if (!res.ok) throw new Error("Failed to fetch live verimeter score");
  return await res.json();
};
export const fetchLinkedClaimsForTaskClaim = async (
  claimId: number,
  viewerId?: number | null
): Promise<LinkedClaim[]> => {
  const qs = viewerId != null ? `?viewerId=${encodeURIComponent(viewerId)}` : "";
  const res = await fetch(
    `${API_BASE_URL}/api/linked-claims-for-claim/${claimId}${qs}`
  );

  if (!res.ok) {
    throw new Error("Failed to fetch linked claims");
  }

  return res.json();
};
//fetch a claim by claim id
//
export async function fetchClaimById(claimId: number): Promise<Claim> {
  const response = await api.get(`${API_BASE_URL}/api/claim/${claimId}`);
  return response.data[0];
}
/**
 * Fetch all claims for a specific task (contentId)
 */
export const fetchClaimsAndLinkedReferencesForTask = async (
  contentId: number,
  viewerId: number | null,
  scope?: 'user' | 'all' | 'admin',
  includeAi = false,
): Promise<ClaimLinks[]> => {
  try {
    const response = await api.get(
      `${API_BASE_URL}/api/claims-and-linked-references/${contentId}`,
      { params: { viewerId, scope, includeAi: includeAi ? 'true' : 'false' } }
    );

    return response.data;
  } catch (error) {
    console.error("❌ Error fetching linked refs:", error);
    throw error;
  }
};
// fetchclaimsources for quiz

/**
 * Fetch all claims for a specific task (contentId)
 */
export const fetchClaimsForTask = async (
  contentId: number,
  viewerId: number | null,
  scope?: 'user' | 'all' | 'admin'
): Promise<Claim[]> => {
  try {
    const response = await api.get(
      `${API_BASE_URL}/api/claims/${contentId}`,
      {
        params: { viewerId, scope },
      }
    );

    return response.data;
  } catch (error) {
    console.error("❌ Error fetching claims:", error);
    return [];
  }
};

export const fetchClaimScoresForTask = async (
  contentId: number,
  viewerId: number | null
): Promise<{ [claimId: number]: number }> => {
  const res = await fetch(
    `${API_BASE_URL}/api/content/${contentId}/claim-scores?viewerId=${
      viewerId ?? ""
    }`
  );
  if (!res.ok) throw new Error("Failed to fetch claim scores");
  return await res.json();
};

//evaluate claims
export async function submitClaimEvaluation(data: {
  claim_id: number;
  reference_id: number;
  evaluation_text: string;
}) {
  return await api.post(`${API_BASE_URL}/api/claim_verifications`, data);
}
/**
 * @deprecated use addClaimToCase() -- this hits the unauthenticated
 * POST /api/claims and never triggers evidence. Kept until every caller
 * migrates.
 */
export const addClaim = async (claimData: {
  claim_text: string;
  veracity_score?: number;
  confidence_level?: number;
  last_verified?: string;
  content_id: number;
  relationship_type?: string;
}) => {
  try {
    const response = await api.post(`${API_BASE_URL}/api/claims`, claimData);
    console.log("✅ Claim added:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Error adding claim:", error);
    throw error;
  }
};

export interface ClaimEvidenceRun {
  claim_evidence_run_id: number;
  content_id: number;
  claim_id: number;
  status: "queued" | "running" | "succeeded" | "failed";
  mode: string;
  attempt_count: number;
  error_message: string | null;
  evidence_count: number | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

export interface AddClaimToCaseResult {
  success: boolean;
  claim: Claim;
  claimId: number;
  reactivated: boolean;
  evidenceRun: { runId: number; status: string } | null;
}

/**
 * Canonical "add a claim to a case" call. Authenticated (JWT identifies the
 * actor), authorized against case membership server-side, dedupes by claim
 * text, and reactivates a previously soft-removed link instead of
 * duplicating it. Pass runEvidence to enqueue a durable evidence run --
 * poll its status with getClaimEvidenceRun().
 */
export const addClaimToCase = async (
  contentId: number,
  claimText: string,
  options: {
    runEvidence?: boolean;
    mode?: "standard" | "deep" | "incremental";
    objectClaimText?: string | null;
    speakerEntity?: string | null;
    articleStance?: string | null;
    argumentFunction?: string | null;
    scoreTransform?: string | null;
  } = {},
): Promise<AddClaimToCaseResult> => {
  const res = await api.post(`${API_BASE_URL}/api/content/${contentId}/claims`, {
    claimText,
    runEvidence: options.runEvidence ?? false,
    mode: options.mode ?? "standard",
    objectClaimText: options.objectClaimText ?? null,
    speakerEntity: options.speakerEntity ?? null,
    articleStance: options.articleStance ?? null,
    argumentFunction: options.argumentFunction ?? null,
    scoreTransform: options.scoreTransform ?? null,
  });
  return res.data;
};

export const getClaimEvidenceRun = async (runId: number): Promise<ClaimEvidenceRun> => {
  const res = await api.get(`${API_BASE_URL}/api/claim-evidence-runs/${runId}`);
  return res.data;
};

/**
 * Poll a claim_evidence_runs row until it reaches succeeded/failed, with
 * bounded backoff so a page left open doesn't poll forever.
 */
export const pollClaimEvidenceRun = async (
  runId: number,
  { intervalMs = 2000, maxAttempts = 30 }: { intervalMs?: number; maxAttempts?: number } = {},
): Promise<ClaimEvidenceRun> => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const run = await getClaimEvidenceRun(runId);
    if (run.status === "succeeded" || run.status === "failed") return run;
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  throw new Error(`Evidence run ${runId} did not finish after ${maxAttempts} polls`);
};

// ✅ Update an existing claim
export const updateClaim = async (claim: Claim): Promise<Claim> => {
  try {
    const response = await api.put(
      `${API_BASE_URL}/api/claims/${claim.claim_id}`,
      claim
    );
    console.log("✅ Claim updated:", response.data);
    return { ...claim }; // or response.data if it returns the whole claim
  } catch (error) {
    console.error("❌ Error updating claim:", error);
    throw error;
  }
};

// ✅ Update claim with evidence re-run option
export const updateClaimWithEvidence = async (
  claim: Claim,
  runEvidence: boolean = true,
  userId?: number
): Promise<{
  success: boolean;
  claim: Claim;
  evidenceRun: boolean;
  summary?: {
    added: number;
    removed: number;
    unchanged: number;
    total: number;
  };
  evidence?: {
    referencesFound: number;
    referencesProcessed: number;
    failedCandidates: Array<{ url: string; title: string; reason: string }>;
  };
}> => {
  try {
    const response = await api.put(
      `${API_BASE_URL}/api/claims/${claim.claim_id}/edit`,
      {
        newText: claim.claim_text,
        runEvidence,
        userId,
      }
    );
    console.log("✅ Claim updated with evidence:", response.data);
    return response.data;
  } catch (error) {
    console.error("❌ Error updating claim with evidence:", error);
    throw error;
  }
};

/**
 * Associate a selected reference with a claim
 */

export const addClaimSource = async (
  claimId: number,
  referenceContentId: number,
  isPrimary: boolean = false,
  userId?: number
): Promise<{ claim_source_id: number }> => {
  const response = await api.post(`${API_BASE_URL}/api/claim-sources`, {
    claim_id: claimId,
    reference_content_id: referenceContentId,
    is_primary: isPrimary,
    user_id: userId,
  });
  return response.data;
};

export const fetchClaimSources = async (
  claimId: number
): Promise<
  {
    claim_source_id: number;
    reference_content_id: number;
    is_primary: boolean;
    created_at: string;
    content_name: string;
    url: string;
  }[]
> => {
  const response = await api.get(
    `${API_BASE_URL}/api/claim-sources/${claimId}`
  );
  return response.data;
};

export const updateClaimSource = async (
  claim_sources_id: number,
  newReferenceId: number,
  notes: string
): Promise<void> => {
  await api.put(`${API_BASE_URL}/api/claim-sources/${claim_sources_id}`, {
    new_reference_id: newReferenceId,
    notes: notes,
  });
};

export const deleteClaimSource = async (
  claim_sources_id: number
): Promise<void> => {
  await api.delete(`${API_BASE_URL}/api/claim-sources/${claim_sources_id}`);
};

/**
 * Update an existing reference
 */
export const updateReference = async (
  reference_content_id: number,
  title: string
) => {
  try {
    console.log("🧪 Sending PUT to updateReference:", {
      title,
      content_id: reference_content_id,
    });
    await api.put(`${API_BASE_URL}/api/updateReference`, {
      content_id: reference_content_id,
      title, // Send title in the request body
    });
    console.log(`✅ Reference ${reference_content_id} updated to "${title}".`);
  } catch (error) {
    console.error("❌ Error updating reference:", error);
  }
};
/**
 * Hide a claim for the current user (soft-delete per user)
 */
export const hideClaim = async (claimId: number, userId: number) => {
  try {
    await api.post(`${API_BASE_URL}/api/claims/hide`, { claimId, userId });
  } catch (error) {
    console.error("❌ Error hiding claim:", error);
    throw error;
  }
};

/**
 * Unhide a claim for the current user
 */
export const unhideClaim = async (claimId: number, userId: number) => {
  try {
    await api.post(`${API_BASE_URL}/api/claims/unhide`, { claimId, userId });
  } catch (error) {
    console.error("❌ Error unhiding claim:", error);
    throw error;
  }
};

/**
 * Delete a claim (deprecated - use hideClaim instead)
 */
export const deleteClaim = async (claimId: number, userId?: number) => {
  if (!userId) {
    console.warn("deleteClaim called without userId - claim will not be hidden");
    return;
  }
  return hideClaim(claimId, userId);
};

/**
 * Permanently delete claims for everyone (super_admin only)
 */
export const hardDeleteClaims = async (claimIds: number[]) => {
  try {
    await api.delete(`${API_BASE_URL}/api/claims/permanent`, {
      data: { claimIds },
    });
  } catch (error) {
    console.error("❌ Error permanently deleting claims:", error);
    throw error;
  }
};

/**
 * Remove a claim from a case's active claim list (soft unlink — the claim,
 * its evidence, and its embeddings are untouched; the case-claim
 * relationship row is preserved with removed_at/removed_by_user_id set, not
 * deleted). Restricted to users assigned to the case; the actor is derived
 * server-side from the JWT, not passed here.
 */
export const removeClaimFromCase = async (contentId: number, claimId: number) => {
  await api.delete(`${API_BASE_URL}/api/content/${contentId}/claims/${claimId}`);
};

/**
 * Run the evidence engine for a single claim already on a case.
 */
export const rerunClaimEvidence = async (
  contentId: number,
  claimId: number,
  claimText: string,
  mode: "standard" | "deep" | "incremental" | "balanced" = "standard",
) => {
  const res = await api.post(
    `${API_BASE_URL}/api/content/${contentId}/claims/${claimId}/evidence/rerun`,
    { mode, claim_text: claimText },
  );
  return res.data;
};

/**
 * Permanently remove references from a task for everyone (super_admin only)
 */
export const hardDeleteReferences = async (
  taskId: number,
  referenceIds: number[],
) => {
  try {
    await api.delete(`${API_BASE_URL}/api/references/permanent`, {
      data: { taskId, referenceIds },
    });
  } catch (error) {
    console.error("❌ Error permanently deleting references:", error);
    throw error;
  }
};

/** --------------------- 📚 REFERENCES FUNCTIONS --------------------- **/
/**
 * Fetch all references from the database.
 */
export const fetchAllReferences = async (searchTerm: string, page: number) => {
  try {
    const searchQuery =
      searchTerm && searchTerm !== "all"
        ? encodeURIComponent(searchTerm)
        : "all";
    const response = await api.get(
      `${API_BASE_URL}/api/references/${searchQuery}?page=${page}`
    );

    console.log("✅ API Response (Page", page, "):", response.data);
    return response.data; // ✅ Returns paginated data
  } catch (error) {
    console.error("❌ Error fetching references:", error);
    return [];
  }
};
/**
 * Fetch all references for a given task (contentId)
 */
export const fetchReferencesForTask = async (contentId: number) => {
  try {
    const response = await api.get(
      `${API_BASE_URL}/api/content/${contentId}/source-references`
    );
    return response.data;
  } catch (error) {
    console.error("❌ Error fetching references:", error);
    return [];
  }
};

export const fetchReferencesWithClaimsForTask = async (
  taskContentId: number,
  viewerId?: number | null,
  scope?: 'user' | 'all' | 'admin'
): Promise<ReferenceWithClaims[]> => {
  const params = new URLSearchParams();
  if (viewerId !== undefined && viewerId !== null) {
    params.append('viewerId', viewerId.toString());
  }
  if (scope) {
    params.append('scope', scope);
  }

  const url = `${API_BASE_URL}/api/content/${taskContentId}/references-with-claims${params.toString() ? '?' + params.toString() : ''}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error("Failed to fetch references with claims");
  }

  return response.json();
};

//Add a link between two claims
export const addClaimLink = async ({
  source_claim_id,
  target_claim_id,
  user_id,
  relationship,
  support_level,
  notes,
  points_earned = 0, // 🎮 GameSpace scoring
  task_content_id,
  source_content_id,
}: {
  source_claim_id: number;
  target_claim_id: number;
  user_id: number;
  relationship: "supports" | "refutes" | "related";
  support_level: number;
  notes: string;
  points_earned?: number;
  task_content_id?: number;
  source_content_id?: number;
}): Promise<{
  message: string;
  claim_link_id: number;
  claim_link_action: "created" | "updated";
  proposal: {
    status: "not_found" | "approved" | "corrected" | "already_approved" | "terminal_unchanged";
    relationship_id: number | null;
  };
  embeddings: {
    source: { status: string; claim_id: number | null; job_id?: number | null; error?: string };
    target: { status: string; claim_id: number | null; job_id?: number | null; error?: string };
  };
  points_earned: number;
}> => {
  try {
    const response = await api.post(`${API_BASE_URL}/api/claim-links`, {
      source_claim_id,
      target_claim_id,
      user_id,
      relationship,
      support_level,
      notes,
      points_earned,
      task_content_id,
      source_content_id,
    });

    // Dispatch event to update activity stats
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('activity-updated'));
    }

    return response.data;
  } catch (error) {
    console.error("❌ Error adding claim link:", error);
    throw error;
  }
};

/**
 * Get total GameSpace score for a user on a specific content/task
 */
export const getClaimLinkScore = async (contentId: number, userId: number): Promise<number> => {
  try {
    const response = await api.get(`${API_BASE_URL}/api/claim-links/score/${contentId}?userId=${userId}`);
    return response.data.totalScore || 0;
  } catch (error) {
    console.error("❌ Error fetching claim link score:", error);
    return 0;
  }
};

/**
 * Remove a reference from a task
 */
export const deleteReferenceFromTask = async (
  taskContentId: number,
  referenceContentId: number
) => {
  try {
    await api.delete(`${API_BASE_URL}/api/remove-content-relation`, {
      data: { taskContentId, referenceContentId },
    });
    console.log(
      `✅ Reference ${referenceContentId} removed from Task ${taskContentId}`
    );
  } catch (error) {
    console.error("❌ Error removing reference from task:", error);
    throw error;
  }
};

/**
 * Hide a reference for the current user (soft-delete)
 */
export const hideReference = async (
  taskContentId: number,
  referenceContentId: number
) => {
  try {
    await api.post(`${API_BASE_URL}/api/references/hide`, {
      taskContentId,
      referenceContentId,
    });
    console.log(
      `✅ Reference ${referenceContentId} hidden from Task ${taskContentId}`
    );
  } catch (error) {
    console.error("❌ Error hiding reference:", error);
    throw error;
  }
};

/**
 * Unhide a reference for the current user
 */
export const unhideReference = async (
  taskContentId: number,
  referenceContentId: number
) => {
  try {
    await api.post(`${API_BASE_URL}/api/references/unhide`, {
      taskContentId,
      referenceContentId,
    });
    console.log(
      `✅ Reference ${referenceContentId} unhidden from Task ${taskContentId}`
    );
  } catch (error) {
    console.error("❌ Error unhiding reference:", error);
    throw error;
  }
};

/**
 * Fetch all references linked to a specific claim
 */
export const fetchClaimReferences = async (claimId: number) => {
  try {
    const response = await api.get(
      `${API_BASE_URL}/api/claims/${claimId}/references`
    );
    return response.data;
  } catch (error) {
    console.error("❌ Error fetching claim references:", error);
    return [];
  }
};

/**
 * Fetch all authors associated with a task.
 */
export const fetchAuthors = async (taskId: number): Promise<Author[]> => {
  try {
    const response = await api.get(
      `${API_BASE_URL}/api/content/${taskId}/authors`
    );
    return response.data;
  } catch (error) {
    console.error("❌ Error fetching authors:", error);
    return [];
  }
};

/**
 * Fetch a single author by author_id.
 */
export const fetchAuthor = async (authorId: number): Promise<Author | null> => {
  try {
    const response = await api.get(
      `${API_BASE_URL}/api/content/${authorId}/author`
    );
    return response.data[0];
  } catch (error) {
    console.error("❌ Error fetching author:", error);
    return null;
  }
};

/**
 * Add authors to content.
 */
export const addAuthorsToContent = async (
  contentId: number,
  authors: Array<{ name: string; description?: string; image?: string }>
): Promise<boolean> => {
  try {
    await api.post(`${API_BASE_URL}/api/content/${contentId}/authors`, {
      contentId,
      authors,
    });
    return true;
  } catch (error) {
    console.error("❌ Error adding authors to content:", error);
    return false;
  }
};

/**
 * Remove an author from content.
 */
export const removeAuthorFromContent = async (
  contentId: number,
  authorId: number
): Promise<boolean> => {
  try {
    await api.delete(
      `${API_BASE_URL}/api/content/${contentId}/authors/${authorId}`
    );
    return true;
  } catch (error) {
    console.error("❌ Error removing author from content:", error);
    return false;
  }
};

/**
 * Add publishers to content.
 */
export const addPublishersToContent = async (
  contentId: number,
  publishers: Array<{ name: string }>
): Promise<boolean> => {
  try {
    await api.post(`${API_BASE_URL}/api/content/${contentId}/publishers`, {
      contentId,
      publisher: publishers[0], // Backend expects single publisher object
    });
    return true;
  } catch (error) {
    console.error("❌ Error adding publishers to content:", error);
    return false;
  }
};

/**
 * Remove a publisher from content.
 */
export const removePublisherFromContent = async (
  contentId: number,
  publisherId: number
): Promise<boolean> => {
  try {
    await api.delete(
      `${API_BASE_URL}/api/content/${contentId}/publishers/${publisherId}`
    );
    return true;
  } catch (error) {
    console.error("❌ Error removing publisher from content:", error);
    return false;
  }
};

/**
 * Fetch all publishers associated with a task.
 */
export const fetchPublishers = async (taskId: number): Promise<Publisher[]> => {
  try {
    const response = await api.get(
      `${API_BASE_URL}/api/content/${taskId}/publishers`
    );
    return response.data;
  } catch (error) {
    console.error("❌ Error fetching publishers:", error);
    return [];
  }
};

/**
 * Fetch a single publisher by publisher_id.
 */
export const fetchPublisher = async (
  publisherId: number
): Promise<Publisher | null> => {
  try {
    const response = await api.get(
      `${API_BASE_URL}/api/content/${publisherId}/publisher`
    );
    return response.data[0];
  } catch (error) {
    console.error("❌ Error fetching author:", error);
    return null;
  }
};
/**
 * Fetch all users in the system.
 */
export const fetchUsers = async (): Promise<User[]> => {
  try {
    const response = await api.get(`${API_BASE_URL}/api/all-users`);
    return response.data;
  } catch (error) {
    console.error("❌ Error fetching users:", error);
    return [];
  }
};

/**
 * Associate a selected reference with a task
 */
export const addReferenceToTask = async (
  taskContentId: number,
  referenceContentId: number
) => {
  try {
    await api.post(`${API_BASE_URL}/api/add-content-relation`, {
      taskContentId,
      referenceContentId,
    });
  } catch (error) {
    console.error("❌ Error adding reference to task:", error);
  }
};

/**
 * Add a reference to a claim
 */
export const addReferenceToClaim = async (
  claimId: number,
  referenceId: number,
  userId: number,
  supportLevel: number
) => {
  try {
    await api.post(`${API_BASE_URL}/api/claims/add-claim-reference`, {
      claimId,
      referenceId,
      userId,
      supportLevel,
    });
  } catch (error) {
    console.error("❌ Error adding reference to claim:", error);
  }
};

/** --------------------- 🔍 SEARCH FUNCTIONS --------------------- **/

/**
 * Search for existing content references by title or URL
 */
export const searchExistingContent = async (query: string) => {
  try {
    const response = await api.get(
      `${API_BASE_URL}/api/search-content?query=${encodeURIComponent(query)}`
    );
    return response.data;
  } catch (error) {
    console.error("❌ Error searching for content:", error);
    return [];
  }
};

/** --------------------- 🌐 SCRAPING FUNCTIONS --------------------- **/

/**
 * Scrape new content from a given URL
 */
export const scrapeNewContent = async (url: string) => {
  try {
    const response = await api.post(`${API_BASE_URL}/api/scrape-content`, {
      url,
    });
    return response.data;
  } catch (error) {
    console.error("❌ Error scraping content:", error);
    return null;
  }
};

/**
 * Scrape a new reference by URL and add it to the database.
 */
export const scrapeAndAddReference = async (url: string, taskId: number) => {
  try {
    // Increase timeout to 5 minutes for AI operations (claim extraction + matching)
    const response = await api.post(
      `${API_BASE_URL}/api/scrape-reference`,
      {
        url,
        taskContentId: taskId, // Fix: use taskContentId instead of taskId
      },
      {
        timeout: 300000, // 5 minutes
      }
    );

    if (response.status === 200) {
      console.log("✅ Scraped reference added:", response.data);
      return response.data;
    }
  } catch (error: unknown) {
    console.error("❌ Error scraping reference:", error);
    if (isAxiosError(error) && error.code === 'ECONNABORTED') {
      console.error("⏱️ Request timed out after 5 minutes");
    }
  }
  return null;
};

/**
 ** Discussions
 **/
export async function postDiscussionEntry(entry: Partial<DiscussionEntry>) {
  const res = await api.post(`${API_BASE_URL}/api/discussion`, entry);

  // Dispatch event to update discussion stats (hot topics, top contributors)
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('discussion-posted'));
  }

  return res.data;
}

export const fetchDiscussionEntries = async (
  contentId: number,
  viewerId?: number | null
): Promise<DiscussionEntry[]> => {
  const params = viewerId !== undefined && viewerId !== null ? { viewerId } : {};
  const res = await api.get(`${API_BASE_URL}/api/discussion/${contentId}`, { params });
  if (Array.isArray(res.data)) return res.data;
  return [];
};

/**
 * Fetch AI evidence links (reference_claim_links) for a task
 * Returns links with support_level, stance, confidence, quote
 */
export const fetchAIEvidenceLinks = async (
  contentId: number
): Promise<import("../../../shared/entities/types").AIEvidenceLink[]> => {
  try {
    const res = await api.get(
      `${API_BASE_URL}/api/reference-claim-links/${contentId}`
    );
    return res.data || [];
  } catch (error) {
    console.error("❌ Error fetching AI evidence links:", error);
    return [];
  }
};

/**
 * Fetch claims with their claim_type field
 * Used to distinguish snippets from regular claims
 */
export const fetchClaimsWithEvidence = async (
  contentId: number,
  viewerId?: number | null,
  scope?: 'user' | 'all' | 'admin'
): Promise<import("../../../shared/entities/types").Claim[]> => {
  try {
    const res = await api.get(
      `${API_BASE_URL}/api/claims-with-evidence/${contentId}`,
      { params: { viewerId, scope } }
    );
    return res.data || [];
  } catch (error) {
    console.error("❌ Error fetching claims with evidence:", error);
    return [];
  }
};

/**
 * Fetch all claims and their references for multiple tasks in one call
 * Much faster than fetching individually
 */
export const fetchBulkClaimsAndReferences = async (
  taskIds: number[],
  viewerId: number | null
): Promise<{
  claimsByTask: import("../../../shared/entities/types").ClaimsByTaskMap;
  claimReferences: import("../../../shared/entities/types").ClaimReferenceMap;
}> => {
  try {
    const res = await api.post(
      `${API_BASE_URL}/api/bulk-claims-and-references`,
      { taskIds, viewerId }
    );
    return res.data || { claimsByTask: {}, claimReferences: {} };
  } catch (error) {
    console.error("❌ Error fetching bulk claims and references:", error);
    return { claimsByTask: {}, claimReferences: {} };
  }
};

/**
 * Fetch failed references for a task
 * These are references that failed to scrape and need manual intervention
 */
export const fetchFailedReferences = async (
  taskContentId: number
): Promise<import("../../../shared/entities/types").FailedReference[]> => {
  try {
    const res = await api.get(
      `${API_BASE_URL}/api/failed-references/${taskContentId}`
    );
    return res.data || [];
  } catch (error) {
    console.error("❌ Error fetching failed references:", error);
    return [];
  }
};

// ── Evaluation ───────────────────────────────────────────────────────────────

export interface PendingEvalUser {
  user_id: number;
  username: string;
  email: string;
  role_name: string;
  role_level: number;
  pending_count: number;
  total_count: number;
  avg_honesty_score: number | null;
}

export interface UsersWithPendingRatingsResult {
  users: PendingEvalUser[];
  evaluator_role: { name: string; level: number };
}

/**
 * Fetch users who have pending claim ratings that the current user can evaluate.
 * Backend enforces role-hierarchy: you only see users at or below your own level.
 */
export const fetchUsersWithPendingRatings =
  async (): Promise<UsersWithPendingRatingsResult> => {
    const res = await api.get(
      `${API_BASE_URL}/api/evaluation/users-with-ratings`,
    );
    // Backend wraps in { success, data }
    return res.data?.data ?? { users: [], evaluator_role: { name: "user", level: 10 } };
  };
